import React from 'react';

import { Context } from 'internal/Provider';

import { useBrowser } from 'utilsTS/hooks';
import { eventHasKey, KEY } from 'utilsTS/dom';

const targets = [
    '[href]',
    'button:not(:disabled)',
    'input:not(:disabled):not([type="hidden"])',
    'select:not(:disabled)',
    'textarea:not(:disabled)',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useFocusDetour<T extends HTMLElement>(isActive?: boolean): React.RefObject<T> {
    const ref = React.useRef<T>(null!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const { document } = useBrowser(React.useContext(Context));

    React.useEffect(() => {
        if (!isActive || !document) return () => {};

        const detourElement = ref.current;

        const detourTabbables = [...detourElement.querySelectorAll<HTMLElement>(targets)];
        const first = detourTabbables[0];
        const last = detourTabbables[detourTabbables.length - 1];

        const pageTabbables = [...document.querySelectorAll<HTMLElement>(targets)];
        const before = document.activeElement as HTMLElement;
        const after = pageTabbables[pageTabbables.indexOf(before) + 1];

        const handleTabKeyPress = (event: KeyboardEvent) => {
            if (!event.shiftKey) {
                if (document.activeElement === before && first) {
                    event.preventDefault();
                    first.focus();
                    return;
                }

                // after !== first is an extra check for end of DOM
                if (document.activeElement === last && after && after !== first) {
                    event.preventDefault();
                    after.focus();
                    return;
                }
            }

            if (event.shiftKey) {
                // after !== first is an extra check for end of DOM
                if (document.activeElement === after && last && after !== first) {
                    event.preventDefault();
                    last.focus();
                    return;
                }

                if (document.activeElement === first && before) {
                    event.preventDefault();
                    before.focus();
                }
            }
        };

        const handleEscapeKeyPress = () => {
            if (detourElement.contains(document.activeElement)) before.focus();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (eventHasKey(event, KEY.TAB)) handleTabKeyPress(event);
            if (eventHasKey(event, KEY.ESC)) handleEscapeKeyPress();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isActive, document]);

    return ref;
}
